import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useParams, useNavigate } from "react-router-dom";

export const AdminCoachMessageReply = () => {
  const { staff } = useStaffAuthContext();
  const { message_id } = useParams(); // Get message_id from URL params
  const navigate = useNavigate();

  const [message, setMessage] = useState(null);
  const [replies, setReplies] = useState([]); // State to hold the replies
  const [reply, setReply] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [isDeleting, setIsDeleting] = useState(false); // State to track if a reply is being deleted
  const [replyToDelete, setReplyToDelete] = useState(null); // State to track the reply being deleted

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch message details and replies in parallel
        const [messageResponse, repliesResponse] = await Promise.all([
          fetch(`${API_URL.GET_COACH_MESSAGE}/${message_id}`, {
            headers: {
              Authorization: `Bearer ${staff.token}`,
            },
          }),
          fetch(`${API_URL.GET_COACH_AMAX_REPLIES_TO_MESSAGE}/${message_id}`, {
            headers: {
              Authorization: `Bearer ${staff.token}`,
            },
          }),
        ]);

        const messageData = await messageResponse.json();
        const repliesData = await repliesResponse.json();

        if (messageResponse.ok) {
          setMessage(messageData.message);
        } else {
          throw new Error(messageData.message || "Failed to fetch message");
        }

        if (repliesResponse.ok) {
          setReplies(repliesData.replies);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [message_id, staff.token]);

  const handleReply = async () => {
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${API_URL.SEND_AMAX_MESSAGE_TO_COACH}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${staff.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sender_id: staff.id,
          sender_type: 'amax',
          receiver_id: message.sender_id,
          receiver_type: 'coach',
          message: reply,
          message_id: message_id,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to send reply");
      }

      setSuccess("Reply sent successfully!");
      setReply(""); // Clear reply input after successful submission

      // Fetch updated replies after sending a new reply
      const repliesResponse = await fetch(`${API_URL.GET_COACH_AMAX_REPLIES_TO_MESSAGE}/${message_id}`, {
        headers: {
          Authorization: `Bearer ${staff.token}`,
        },
      });
      const repliesData = await repliesResponse.json();

      if (repliesResponse.ok) {
        setReplies(repliesData.replies);
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteReply = (reply_id) => {
    setReplyToDelete(reply_id); // Set the reply to be deleted
    setIsDeleting(true); // Show confirmation modal
  };

  const confirmDeleteReply = async () => {
    setIsLoading(true);
    setError("");
    try {
      // Call the API to delete the reply
      const response = await fetch(`${API_URL.DELETE_COACH_MESSAGE}/${replyToDelete}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${staff.token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to delete reply");
      }

      setSuccess("Reply deleted successfully!");

      // Update the UI by removing the deleted reply
      setReplies(replies.filter((reply) => reply._id !== replyToDelete));

      // Close confirmation modal
      setIsDeleting(false);
      setReplyToDelete(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDeleteReply = () => {
    setIsDeleting(false); // Close confirmation modal without deleting
    setReplyToDelete(null);
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Go Back
        </button>

        {error && (
          <div className="bg-red-100 text-red-500 p-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 text-green-500 p-3 rounded mb-4">
            {success}
          </div>
        )}

        <h1 className="text-2xl font-bold mb-4">Coach Message</h1>

        {/* Message Display */}
        {message ? (
          <div className="bg-white p-4 rounded shadow mb-4">
            <h2 className="font-bold text-lg">{message.senderName}</h2>
            <p className="text-gray-700">{message.message}</p>
            <p className="text-sm text-gray-500">{new Date(message.timestamp).toLocaleString()}</p>
          </div>
        ) : (
          <p>Loading message...</p>
        )}

        {/* Display existing replies */}
        <div className="bg-white p-4 rounded shadow mb-4 min-h-[200px]">
          <h2 className="text-xl font-bold mb-4">Replies</h2>
          {replies.length > 0 ? (
            replies.map((reply, index) => (
              <div key={index} className="border-b pb-4 mb-4">
                <p className="font-semibold">{reply.senderName}</p>
                <p>{reply.message}</p>
                <p className="text-sm text-gray-500">{new Date(reply.timestamp).toLocaleString()}</p>
                <button
                  onClick={() => handleDeleteReply(reply._id)}
                  className="text-red-600 mt-2"
                >
                  Delete Reply
                </button>
              </div>
            ))
          ) : (
            <p>No replies yet.</p>
          )}
        </div>

        {/* Confirmation Modal for Deleting a Reply */}
        {isDeleting && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-lg max-w-sm w-full">
              <h2 className="text-xl font-bold mb-4">Are you sure you want to delete this reply?</h2>
              <div className="flex justify-end">
                <button
                  onClick={cancelDeleteReply}
                  className="px-4 py-2 bg-gray-400 text-white rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteReply}
                  className="px-4 py-2 bg-red-600 text-white rounded"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        <h2 className="text-xl font-bold mb-4">Reply</h2>
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          className="w-full p-2 border rounded mb-4"
          placeholder="Enter your reply"
          rows="4"
        ></textarea>

        <button
          onClick={handleReply}
          className={`px-4 py-2 mt-4 bg-blue-500 text-white rounded hover:bg-blue-600 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={isLoading}
        >
          {isLoading ? "Sending..." : "Send Reply"}
        </button>
      </div>
    </div>
  );
};
